import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import InstructorMessaging from './InstructorMessaging';
import WorkshopAnalytics from './WorkshopAnalytics';

function MobileOptimizedDashboard() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [workshops, setWorkshops] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalBookings: 0,
    upcomingWorkshops: 0,
    unreadMessages: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      fetchDashboardData();
    }
  }, [currentUser]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Fetch dashboard data
      const [workshopsRes, bookingsRes] = await Promise.all([
        fetch(`/api/sessions/workshops?instructor_id=${currentUser.uid}`),
        fetch(`/api/sessions/bookings?instructor_id=${currentUser.uid}`)
      ]);

      const workshopsData = await workshopsRes.json();
      const bookingsData = await bookingsRes.json();

      setWorkshops(workshopsData);
      setBookings(bookingsData);

      // Calculate stats
      const confirmedBookings = bookingsData.filter(b => b.status === 'confirmed');
      const totalRevenue = confirmedBookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0);
      const upcomingWorkshops = workshopsData.filter(w => 
        new Date(w.startDate) > new Date()
      ).length;

      setStats({
        totalRevenue,
        totalBookings: confirmedBookings.length,
        upcomingWorkshops,
        unreadMessages: 3 // Would fetch from messaging API
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'workshops', label: 'Workshops', icon: '📷' },
    { id: 'bookings', label: 'Bookings', icon: '📅' },
    { id: 'messages', label: 'Messages', icon: '💬', badge: stats.unreadMessages },
    { id: 'analytics', label: 'Analytics', icon: '📈' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8 shadow-sm text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Dashboard</h1>
              <p className="text-sm text-gray-500">Welcome back!</p>
            </div>
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white font-medium">
                {currentUser?.email?.[0]?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Mobile Tab Navigation */}
        <div className="flex overflow-x-auto scrollbar-hide bg-gray-50 px-2 py-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 flex flex-col items-center px-3 py-2 mx-1 rounded-lg text-xs transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="text-lg mb-1">{tab.icon}</span>
              <span className="whitespace-nowrap">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-panel p-4 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-klorah-text-light tracking-wider">REVENUE</p>
                    <p className="text-2xl font-bold text-white">
                      {formatCurrency(stats.totalRevenue)}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-emerald-500/20 border border-emerald-500/30 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="glass-panel p-4 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-klorah-text-light tracking-wider">BOOKINGS</p>
                    <p className="text-2xl font-bold text-white">{stats.totalBookings}</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-500/20 border border-blue-500/30 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="glass-panel p-4 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-klorah-text-light tracking-wider">UPCOMING</p>
                    <p className="text-2xl font-bold text-white">{stats.upcomingWorkshops}</p>
                  </div>
                  <div className="w-10 h-10 bg-purple-500/20 border border-purple-500/30 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="glass-panel p-4 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-klorah-text-light tracking-wider">MESSAGES</p>
                    <p className="text-2xl font-bold text-white">{stats.unreadMessages}</p>
                  </div>
                  <div className="w-10 h-10 bg-klorah-primary/20 border border-klorah-primary/30 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-klorah-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="glass-panel p-4 rounded-xl">
              <h3 className="text-lg font-semibold text-white mb-4 tracking-wider">QUICK ACTIONS</h3>
              <div className="grid grid-cols-2 gap-3">
                <button className="flex flex-col items-center p-4 bg-klorah-primary/10 border border-klorah-primary/20 rounded-xl text-center hover:bg-klorah-primary/20 transition-colors">
                  <svg className="w-6 h-6 text-klorah-primary mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="text-sm font-medium text-klorah-primary tracking-wider">NEW WORKSHOP</span>
                </button>
                <button className="flex flex-col items-center p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center hover:bg-emerald-500/20 transition-colors">
                  <svg className="w-6 h-6 text-emerald-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span className="text-sm font-medium text-emerald-400 tracking-wider">VIEW BOOKINGS</span>
                </button>
                <button className="flex flex-col items-center p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl text-center hover:bg-purple-500/20 transition-colors">
                  <svg className="w-6 h-6 text-purple-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="text-sm font-medium text-purple-400 tracking-wider">ANALYTICS</span>
                </button>
                <button className="flex flex-col items-center p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl text-center hover:bg-blue-500/20 transition-colors">
                  <svg className="w-6 h-6 text-blue-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-400 tracking-wider">SETTINGS</span>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="glass-panel p-4 rounded-xl">
              <h3 className="text-lg font-semibold text-white mb-4 tracking-wider">RECENT ACTIVITY</h3>
              <div className="space-y-3">
                {bookings.slice(0, 3).map((booking, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">
                        New booking from {booking.participantName}
                      </p>
                      <p className="text-xs text-klorah-text">
                        {formatDate(booking.createdAt)} • {formatCurrency(booking.totalAmount)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'workshops' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Workshops</h2>
              <button className="klorah-button text-sm">
                + New Workshop
              </button>
            </div>
            
            {workshops.map((workshop) => (
              <div key={workshop.id} className="glass-panel p-4 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">{workshop.title}</h3>
                    <p className="text-sm text-klorah-text">{workshop.location}</p>
                    <p className="text-sm text-klorah-text-light">
                      {formatDate(workshop.startDate)} - {formatDate(workshop.endDate)}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    workshop.status === 'published' 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                      : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  }`}>
                    {workshop.status}
                  </span>
                </div>
                
                <div className="flex justify-between items-center text-sm">
                  <span className="text-klorah-text">
                    {workshop.currentParticipants || 0}/{workshop.maxParticipants} spots
                  </span>
                  <span className="font-semibold text-klorah-primary">
                    {formatCurrency(workshop.price || 0)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'bookings' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Recent Bookings</h2>
            
            {bookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900">{booking.participantName}</h3>
                    <p className="text-sm text-gray-600">{booking.participantEmail}</p>
                    <p className="text-sm text-gray-500">{booking.workshopTitle}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {formatCurrency(booking.totalAmount)}
                    </p>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                      booking.status === 'confirmed' 
                        ? 'bg-green-100 text-green-800'
                        : booking.status === 'pending_payment'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'messages' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Messages</h2>
            <InstructorMessaging />
          </div>
        )}

        {activeTab === 'analytics' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Analytics</h2>
            <WorkshopAnalytics instructorId={currentUser?.uid} />
          </div>
        )}
      </div>

      {/* Bottom Safe Area for Mobile */}
      <div className="h-safe-bottom"></div>
    </div>
  );
}

export default MobileOptimizedDashboard; 